import {
  useIsPlatformAdmin,
  useOrganization,
  useSession,
  useUser,
} from "@/contexts/Auth";
import { useSdkClient, useSlugs } from "@/contexts/Sdk";
import { useRBAC } from "@/hooks/useRBAC";
import { getAdminServerUrl } from "@/lib/admin-server-url";
import { DEMO_ORG_SLUG } from "@/lib/demo";
import { logoutToLogin } from "@/lib/logout-to-login";
import { useOrgRoutes, useRoutes } from "@/routes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { ThemeSwitcher } from "@/components/ui/ThemeSwitcher";
import {
  ActivityIcon,
  BookOpenIcon,
  BuildingIcon,
  CompassIcon,
  CreditCardIcon,
  CrownIcon,
  LogOutIcon,
  MailIcon,
  MapIcon,
  MessageCircleIcon,
  PencilIcon,
  SettingsIcon,
} from "lucide-react";
import { useState, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router";
import { useIdentityHrefBuilder } from "@/lib/useIdentityHref";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import {
  isPylonChatOpen,
  subscribePylonChatOpen,
  togglePylonChat,
} from "@/lib/pylon";

export function SidebarUserMenu(): JSX.Element {
  const user = useUser();
  const isPlatformAdmin = useIsPlatformAdmin();
  const adminServerUrl = getAdminServerUrl(
    document
      .querySelector<HTMLMetaElement>('meta[name="gram-admin-server-url"]')
      ?.getAttribute("content"),
    import.meta.env.DEV,
  );
  const session = useSession();
  const organization = useOrganization();
  const navigate = useNavigate();
  const routes = useRoutes();
  const orgRoutes = useOrgRoutes();
  const client = useSdkClient();
  const { projectSlug } = useSlugs();
  const { hasAnyScope } = useRBAC();
  const identityHref = useIdentityHrefBuilder()({ userId: user.id });

  const canAccessOrgRoutes = hasAnyScope(["org:read", "org:admin"]);
  const isMultiOrg = session.organizations.length > 1;
  const isDemoOrg = organization.slug === DEMO_ORG_SLUG;

  const [menuOpen, setMenuOpen] = useState(false);
  const pylonOpen = useSyncExternalStore(
    subscribePylonChatOpen,
    isPylonChatOpen,
    () => false,
  );

  const userInitials =
    user.displayName
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) ||
    user.email?.slice(0, 2).toUpperCase() ||
    "?";

  // Only the first name in the compact footer preview so it never truncates.
  const firstName = user.displayName?.trim().split(/\s+/)[0] || "User";
  const accountDetails = (
    <div className="flex min-w-0 flex-col space-y-1">
      <p className="truncate text-sm leading-none font-medium">
        {user.displayName || "User"}
      </p>
      <p className="text-muted-foreground truncate text-xs leading-none">
        {user.email}
      </p>
    </div>
  );

  return (
    <div className="flex items-center gap-2 px-1 py-1">
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {/* The identity preview is the menu trigger — collapsed it is the avatar
            alone, so the menu stays reachable without a second control. */}
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-trigger"
            type="button"
            aria-label="Account menu"
            className="hover:bg-accent flex min-w-0 flex-1 items-center gap-2 p-1 text-left group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:justify-center"
          >
            <Avatar className="size-7 shrink-0">
              <AvatarImage
                src={user.photoUrl}
                alt={user.displayName || user.email}
              />
              <AvatarFallback className="text-xs">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <span className="truncate text-sm font-medium group-data-[collapsible=icon]:hidden">
              {firstName}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" className="w-56">
          <DropdownMenuGroup className="relative">
            {identityHref ? (
              <DropdownMenuItem asChild className="pr-8 font-normal">
                <Link to={identityHref}>{accountDetails}</Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuLabel className="pr-8 font-normal">
                {accountDetails}
              </DropdownMenuLabel>
            )}
            {isPlatformAdmin && adminServerUrl && (
              <DropdownMenuItem
                asChild
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1.5 right-2 size-4 cursor-pointer p-0 focus:bg-transparent focus:text-foreground focus-visible:rounded focus-visible:ring-2 focus-visible:ring-offset-1"
              >
                <a
                  href={adminServerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Platform admin"
                >
                  <CrownIcon className="h-4 w-4" />
                </a>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {projectSlug && (
              <DropdownMenuItem onClick={() => routes.settings.goTo()}>
                <SettingsIcon className="mr-2 h-4 w-4" />
                Project Settings
              </DropdownMenuItem>
            )}
            {canAccessOrgRoutes && (
              <DropdownMenuItem onClick={() => orgRoutes.billing.goTo()}>
                <CreditCardIcon className="mr-2 h-4 w-4" />
                Billing
              </DropdownMenuItem>
            )}
            {isMultiOrg && (
              <DropdownMenuItem
                onClick={() => {
                  void navigate("/switch-org");
                }}
              >
                <BuildingIcon className="mr-2 h-4 w-4" />
                Switch Organization
              </DropdownMenuItem>
            )}
            {!isDemoOrg && (
              <DropdownMenuItem onClick={() => routes.exploreDemo.goTo()}>
                <CompassIcon className="mr-2 h-4 w-4" />
                Explore demo org
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <a
                href="https://www.speakeasy.com/docs/mcp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BookOpenIcon className="mr-2 h-4 w-4" />
                Docs
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href="https://www.speakeasy.com/changelog?product=mcp-platform"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PencilIcon className="mr-2 h-4 w-4" />
                Changelog
              </a>
            </DropdownMenuItem>
            {"Pylon" in window && (
              <DropdownMenuItem onClick={togglePylonChat}>
                <MessageCircleIcon className="mr-2 h-4 w-4" />
                {pylonOpen ? "Close Support" : "Get Support"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <a href="mailto:gram@speakeasy.com">
                <MailIcon className="mr-2 h-4 w-4" />
                Email Team
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href="https://roadmap.speakeasy.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapIcon className="mr-2 h-4 w-4" />
                Roadmap
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href="https://status.speakeasy.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ActivityIcon className="mr-2 h-4 w-4" />
                Platform Status
              </a>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              void logoutToLogin(client);
            }}
          >
            <LogOutIcon className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Smaller inline theme switcher — expanded only */}
      <ThemeSwitcher className="group-data-[collapsible=icon]:hidden" />
    </div>
  );
}
